import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'motion/react';
import { Mail, Eye, Archive, Trash2, MessageCircle, Clock, Filter, RefreshCw, CheckCircle, Lock, LogOut, FileText, Briefcase, Globe, AlertCircle, Search, ArrowUpDown, Download, CheckSquare } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/layout/Layout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Text Reveal Animation
const TextReveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

const statusColors = {
  new: 'bg-green-100 text-green-700 border-green-200',
  read: 'bg-blue-100 text-blue-700 border-blue-200',
  replied: 'bg-purple-100 text-purple-700 border-purple-200',
  archived: 'bg-gray-100 text-gray-700 border-gray-200'
};

const statusIcons = {
  new: Mail,
  read: Eye,
  replied: CheckCircle,
  archived: Archive
};

// PIN Login Component
const PinLogin = ({ onSuccess }) => {
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/contact/verify-pin`, {
        method: 'POST',
        headers: {
          'X-Admin-Pin': pin
        }
      });
      
      const data = await response.json();
      
      if (data.valid) {
        sessionStorage.setItem('adminPin', pin);
        onSuccess(pin);
        toast.success('Access granted!');
      } else {
        setError('Invalid PIN. Please try again.');
        setPin('');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <section className="min-h-screen bg-sunrise-hero flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0">
          <motion.div 
            className="absolute top-20 right-[15%] w-[400px] h-[400px] sun-orb sun-orb-primary"
            animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 8, repeat: Infinity }}
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 w-full max-w-md mx-4"
        >
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-[#FF6B35]/10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FFB627] flex items-center justify-center mx-auto mb-4">
                <Lock className="text-white" size={28} />
              </div>
              <h1 className="font-display text-2xl text-[#2D2D2D] mb-2">Admin Access</h1>
              <p className="text-[#5A5A5A] text-sm">Enter your PIN to access the dashboard</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-[#5A5A5A] text-sm mb-2">Admin PIN</label>
                <input
                  type="password"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  placeholder="Enter PIN"
                  className="input-warm text-center text-2xl tracking-[0.5em] font-mono"
                  autoFocus
                  data-testid="admin-pin-input"
                />
              </div>

              {error && (
                <p className="text-red-500 text-sm text-center">{error}</p>
              )}

              <motion.button
                type="submit"
                disabled={loading || pin.length < 4}
                className="btn-sunrise w-full py-4 disabled:opacity-50"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="admin-login-btn"
              >
                {loading ? 'Verifying...' : 'Access Dashboard'}
              </motion.button>
            </form>

            <p className="text-[#8A8A8A] text-xs text-center mt-6">
              Contact your administrator if you forgot the PIN
            </p>
          </div>
        </motion.div>
      </section>
    </Layout>
  );
};

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminPin, setAdminPin] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [auditSubmissions, setAuditSubmissions] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, read: 0, replied: 0 });
  const [auditStats, setAuditStats] = useState({ total: 0, new: 0, read: 0, replied: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [activeTab, setActiveTab] = useState('contact');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [selectedIds, setSelectedIds] = useState(new Set());

  useEffect(() => {
    // Check for existing session
    const storedPin = sessionStorage.getItem('adminPin');
    if (storedPin) {
      verifyStoredPin(storedPin);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyStoredPin = async (pin) => {
    try {
      const response = await fetch(`${API_URL}/api/contact/verify-pin`, {
        method: 'POST',
        headers: { 'X-Admin-Pin': pin }
      });
      const data = await response.json();
      
      if (data.valid) {
        setAdminPin(pin);
        setIsAuthenticated(true);
      } else {
        sessionStorage.removeItem('adminPin');
      }
    } catch (err) {
      sessionStorage.removeItem('adminPin');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = (pin) => {
    setAdminPin(pin);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminPin');
    setIsAuthenticated(false);
    setAdminPin('');
    setSubmissions([]);
    setAuditSubmissions([]);
    setStats({ total: 0, new: 0, read: 0, replied: 0 });
    setAuditStats({ total: 0, new: 0, read: 0, replied: 0 });
  };

  useEffect(() => {
    if (isAuthenticated && adminPin) {
      fetchData();
    }
  }, [isAuthenticated, adminPin]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { 'X-Admin-Pin': adminPin };
      
      const [submissionsRes, statsRes, auditSubmissionsRes, auditStatsRes] = await Promise.all([
        fetch(`${API_URL}/api/contact/submissions`, { headers }),
        fetch(`${API_URL}/api/contact/submissions/stats`, { headers }),
        fetch(`${API_URL}/api/audit/submissions`, { headers }),
        fetch(`${API_URL}/api/audit/submissions/stats`, { headers })
      ]);
      
      if (submissionsRes.status === 401 || statsRes.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
        return;
      }
      
      const submissionsData = await submissionsRes.json();
      const statsData = await statsRes.json();
      const auditSubmissionsData = await auditSubmissionsRes.json();
      const auditStatsData = await auditStatsRes.json();
      
      setSubmissions(submissionsData);
      setStats(statsData);
      setAuditSubmissions(auditSubmissionsData);
      setAuditStats(auditStatsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    const endpoint = activeTab === 'contact' 
      ? `${API_URL}/api/contact/submissions/${id}/status?status=${newStatus}`
      : `${API_URL}/api/audit/submissions/${id}/status?status=${newStatus}`;
    
    try {
      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers: { 'X-Admin-Pin': adminPin }
      });
      
      if (response.status === 401) {
        handleLogout();
        return;
      }
      
      if (response.ok) {
        if (activeTab === 'contact') {
          setSubmissions(prev => prev.map(s => s.id === id ? { ...s, status: newStatus } : s));
        } else {
          setAuditSubmissions(prev => prev.map(s => s.id === id ? { ...s, status: newStatus } : s));
        }
        fetchData(); // Refresh stats
        toast.success('Status updated');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    
    const endpoint = activeTab === 'contact'
      ? `${API_URL}/api/contact/submissions/${id}`
      : `${API_URL}/api/audit/submissions/${id}`;
    
    try {
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: { 'X-Admin-Pin': adminPin }
      });
      
      if (response.status === 401) {
        handleLogout();
        return;
      }
      
      if (response.ok) {
        if (activeTab === 'contact') {
          setSubmissions(prev => prev.filter(s => s.id !== id));
        } else {
          setAuditSubmissions(prev => prev.filter(s => s.id !== id));
        }
        setSelectedSubmission(null);
        toast.success('Submission deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete submission');
    }
  };

  // Show loading state
  if (loading && !isAuthenticated) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
          <motion.div
            className="w-12 h-12 border-2 border-[#FF6B35]/30 border-t-[#FF6B35] rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
        </div>
      </Layout>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <PinLogin onSuccess={handleLoginSuccess} />;
  }

  const currentList = activeTab === 'contact' ? submissions : auditSubmissions;
  const filteredSubmissions = currentList
    .filter(s => selectedFilter === 'all' || s.status === selectedFilter)
    .filter(s => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (s.name || '').toLowerCase().includes(q) ||
        (s.email || '').toLowerCase().includes(q) ||
        (s.message || '').toLowerCase().includes(q) ||
        (s.company || '').toLowerCase().includes(q) ||
        (s.service || '').toLowerCase().includes(q) ||
        (s.website_url || '').toLowerCase().includes(q);
    })
    .sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

  const currentStats = activeTab === 'contact' ? stats : auditStats;

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredSubmissions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredSubmissions.map(s => s.id)));
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const bulkUpdateStatus = async (newStatus) => {
    for (const id of selectedIds) {
      await updateStatus(id, newStatus);
    }
    setSelectedIds(new Set());
    toast.success(`${selectedIds.size} items marked as ${newStatus}`);
  };

  const exportCSV = () => {
    const data = filteredSubmissions;
    if (!data.length) { toast.error('No data to export'); return; }
    const headers = activeTab === 'contact'
      ? ['Name', 'Email', 'Phone', 'Service', 'Message', 'Status', 'Date']
      : ['Name', 'Email', 'Company', 'Website', 'Focus Areas', 'Notes', 'Status', 'Date'];
    const rows = data.map(s => activeTab === 'contact'
      ? [s.name, s.email, s.phone || '', s.service || '', (s.message || '').replace(/,/g, ';'), s.status, s.created_at || '']
      : [s.name, s.email, s.company || '', s.website_url || '', (s.focus_areas || []).join('; '), (s.notes || '').replace(/,/g, ';'), s.status, s.created_at || '']
    );
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeTab}-submissions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported to CSV');
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Layout>
      {/* HERO SECTION */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16 bg-sunrise-hero relative overflow-hidden">
        <div className="absolute inset-0">
          <motion.div 
            className="absolute top-20 right-[15%] w-[400px] h-[400px] sun-orb sun-orb-primary"
            animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 8, repeat: Infinity }}
          />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <div className="flex items-start justify-between">
            <div>
              <TextReveal>
                <span className="inline-block px-4 py-1.5 bg-[#FF6B35]/10 text-[#FF6B35] text-sm font-medium rounded-full mb-6">Admin Dashboard</span>
              </TextReveal>
              
              <TextReveal delay={0.1}>
                <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#2D2D2D] leading-tight mb-4">
                  {activeTab === 'contact' ? 'Contact' : 'Audit'} <span className="text-gradient-sunrise">Submissions</span>
                </h1>
              </TextReveal>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-lg text-[#5A5A5A] max-w-xl"
              >
                {activeTab === 'contact' 
                  ? 'View and manage all contact form submissions from your website.'
                  : 'View and manage all audit request submissions from your website.'}
              </motion.p>
            </div>

            <motion.button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-[#FF6B35]/20 rounded-full text-[#5A5A5A] hover:text-[#FF6B35] hover:border-[#FF6B35] transition-colors"
              whileHover={{ scale: 1.02 }}
              data-testid="admin-logout-btn"
            >
              <LogOut size={16} />
              Logout
            </motion.button>
          </div>
        </div>
      </section>

      {/* TABS */}
      <section className="py-4 bg-white border-b border-[#FF6B35]/10">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex items-center gap-4">
            <button
              onClick={() => { setActiveTab('contact'); setSelectedSubmission(null); setSelectedFilter('all'); }}
              data-testid="tab-contact"
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
                activeTab === 'contact'
                  ? 'bg-gradient-to-r from-[#FF6B35] to-[#FFB627] text-white shadow-lg shadow-orange-500/20'
                  : 'bg-[#FFFBF5] text-[#5A5A5A] border border-[#FF6B35]/15 hover:border-[#FF6B35]/30'
              }`}
            >
              <Mail size={18} />
              Contact Messages
              {stats.new > 0 && (
                <span className={`ml-1 px-2 py-0.5 text-xs rounded-full ${activeTab === 'contact' ? 'bg-white/20' : 'bg-green-100 text-green-700'}`}>
                  {stats.new}
                </span>
              )}
            </button>
            <button
              onClick={() => { setActiveTab('audit'); setSelectedSubmission(null); setSelectedFilter('all'); }}
              data-testid="tab-audit"
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
                activeTab === 'audit'
                  ? 'bg-gradient-to-r from-[#FF6B35] to-[#FFB627] text-white shadow-lg shadow-orange-500/20'
                  : 'bg-[#FFFBF5] text-[#5A5A5A] border border-[#FF6B35]/15 hover:border-[#FF6B35]/30'
              }`}
            >
              <FileText size={18} />
              Audit Requests
              {auditStats.new > 0 && (
                <span className={`ml-1 px-2 py-0.5 text-xs rounded-full ${activeTab === 'audit' ? 'bg-white/20' : 'bg-green-100 text-green-700'}`}>
                  {auditStats.new}
                </span>
              )}
            </button>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-6 bg-white border-b border-[#FF6B35]/10">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#FF6B35]/10 flex items-center justify-center">
                <MessageCircle className="text-[#FF6B35]" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#2D2D2D]">{currentStats.total}</p>
                <p className="text-xs text-[#8A8A8A]">Total</p>
              </div>
            </div>
            
            <div className="w-px h-8 bg-[#FF6B35]/20" />
            
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Mail className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{currentStats.new}</p>
                <p className="text-xs text-[#8A8A8A]">New</p>
              </div>
            </div>
            
            <div className="w-px h-8 bg-[#FF6B35]/20" />
            
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Eye className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{currentStats.read}</p>
                <p className="text-xs text-[#8A8A8A]">Read</p>
              </div>
            </div>
            
            <div className="w-px h-8 bg-[#FF6B35]/20" />
            
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <CheckCircle className="text-purple-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-600">{currentStats.replied}</p>
                <p className="text-xs text-[#8A8A8A]">Replied</p>
              </div>
            </div>

            <div className="ml-auto">
              <button
                onClick={fetchData}
                className="flex items-center gap-2 px-4 py-2 bg-[#FFFBF5] border border-[#FF6B35]/20 rounded-full text-[#5A5A5A] hover:border-[#FF6B35] transition-colors"
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FILTERS + SEARCH + ACTIONS */}
      <section className="py-4 bg-[#FFFBF5] border-b border-[#FF6B35]/10">
        <div className="container mx-auto px-6 md:px-12 space-y-3">
          {/* Search + Sort + Export */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 max-w-sm">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A]" />
              <input
                type="text"
                placeholder="Search by name, email, company..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 rounded-full bg-white border border-[#FF6B35]/15 text-sm text-[#2D2D2D] focus:outline-none focus:border-[#FF6B35]/50"
                data-testid="admin-search-input"
              />
            </div>
            <button
              onClick={() => setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest')}
              className="flex items-center gap-1.5 px-4 py-2 bg-white border border-[#FF6B35]/15 rounded-full text-sm text-[#5A5A5A] hover:border-[#FF6B35]/30 transition-colors"
              data-testid="sort-toggle"
            >
              <ArrowUpDown size={14} /> {sortOrder === 'newest' ? 'Newest First' : 'Oldest First'}
            </button>
            <button
              onClick={exportCSV}
              className="flex items-center gap-1.5 px-4 py-2 bg-white border border-[#FF6B35]/15 rounded-full text-sm text-[#5A5A5A] hover:border-[#FF6B35]/30 transition-colors"
              data-testid="export-csv-btn"
            >
              <Download size={14} /> Export CSV
            </button>
          </div>

          {/* Filters + Bulk Actions */}
          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={16} className="text-[#8A8A8A]" />
            {['all', 'new', 'read', 'replied', 'archived'].map((filter) => (
              <button
                key={filter}
                onClick={() => setSelectedFilter(filter)}
                data-testid={`filter-${filter}`}
                className={`px-4 py-2 rounded-full text-sm capitalize transition-all ${
                  selectedFilter === filter
                    ? 'bg-gradient-to-r from-[#FF6B35] to-[#FFB627] text-white font-medium'
                    : 'bg-white text-[#5A5A5A] border border-[#FF6B35]/15 hover:border-[#FF6B35]/30'
                }`}
              >
                {filter}
              </button>
            ))}

            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2 ml-4 pl-4 border-l border-[#FF6B35]/20">
                <span className="text-sm text-[#5A5A5A]">{selectedIds.size} selected</span>
                <button onClick={() => bulkUpdateStatus('read')} data-testid="bulk-mark-read"
                  className="px-3 py-1.5 bg-blue-100 text-blue-700 text-xs rounded-full hover:bg-blue-200 transition-colors">Mark Read</button>
                <button onClick={() => bulkUpdateStatus('replied')} data-testid="bulk-mark-replied"
                  className="px-3 py-1.5 bg-purple-100 text-purple-700 text-xs rounded-full hover:bg-purple-200 transition-colors">Mark Replied</button>
                <button onClick={() => bulkUpdateStatus('archived')} data-testid="bulk-archive"
                  className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs rounded-full hover:bg-gray-200 transition-colors">Archive</button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* SUBMISSIONS LIST */}
      <section className="section-padding bg-[#FFFBF5]">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* List */}
            <div className="lg:col-span-1 space-y-3">
              {!loading && filteredSubmissions.length > 0 && (
                <div className="flex items-center gap-2 px-2 mb-1">
                  <button onClick={toggleSelectAll} data-testid="select-all-btn"
                    className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                      selectedIds.size === filteredSubmissions.length && filteredSubmissions.length > 0
                        ? 'bg-[#FF6B35] border-[#FF6B35]' : 'border-[#8A8A8A]/30 hover:border-[#FF6B35]'
                    }`}>
                    {selectedIds.size === filteredSubmissions.length && filteredSubmissions.length > 0 && <CheckSquare size={12} className="text-white" />}
                  </button>
                  <span className="text-xs text-[#8A8A8A]">Select all ({filteredSubmissions.length})</span>
                </div>
              )}
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <div key={i} className="p-4 bg-white rounded-xl border border-[#FF6B35]/10 animate-pulse">
                    <div className="h-4 bg-[#FF6B35]/5 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-[#FF6B35]/5 rounded w-1/2" />
                  </div>
                ))
              ) : filteredSubmissions.length === 0 ? (
                <div className="text-center py-12">
                  <Mail size={48} className="mx-auto text-[#8A8A8A] mb-4" />
                  <p className="text-[#5A5A5A]">No submissions found</p>
                </div>
              ) : (
                filteredSubmissions.map((submission) => {
                  const StatusIcon = statusIcons[submission.status];
                  return (
                    <motion.div
                      key={submission.id}
                      className={`flex items-start gap-2 ${selectedSubmission?.id === submission.id ? '' : ''}`}
                    >
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleSelect(submission.id); }}
                        className={`mt-4 w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
                          selectedIds.has(submission.id) ? 'bg-[#FF6B35] border-[#FF6B35]' : 'border-[#8A8A8A]/30 hover:border-[#FF6B35]'
                        }`}
                        data-testid={`select-${submission.id}`}
                      >
                        {selectedIds.has(submission.id) && <CheckSquare size={12} className="text-white" />}
                      </button>
                      <motion.div
                        onClick={() => {
                          setSelectedSubmission(submission);
                          if (submission.status === 'new') {
                            updateStatus(submission.id, 'read');
                          }
                        }}
                        className={`flex-1 p-4 bg-white rounded-xl border cursor-pointer transition-all ${
                          selectedSubmission?.id === submission.id
                            ? 'border-[#FF6B35] shadow-lg shadow-orange-500/10'
                            : 'border-[#FF6B35]/10 hover:border-[#FF6B35]/30'
                        }`}
                        whileHover={{ scale: 1.01 }}
                      >
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-[#2D2D2D] truncate flex-1">{submission.name}</h4>
                        <span className={`px-2 py-0.5 text-xs rounded-full border ${statusColors[submission.status]} flex items-center gap-1`}>
                          <StatusIcon size={10} />
                          {submission.status}
                        </span>
                      </div>
                      <p className="text-sm text-[#5A5A5A] truncate">{submission.email}</p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-[#8A8A8A]">
                        <Clock size={12} />
                        {formatDate(submission.submitted_at)}
                      </div>
                    </motion.div>
                    </motion.div>
                  );
                })
              )}
            </div>

            {/* Detail View */}
            <div className="lg:col-span-2">
              {selectedSubmission ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl border border-[#FF6B35]/10 p-6 md:p-8 sticky top-28"
                >
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="font-display text-2xl text-[#2D2D2D]">{selectedSubmission.name}</h2>
                      <a 
                        href={`mailto:${selectedSubmission.email}`}
                        className="text-[#FF6B35] hover:underline"
                      >
                        {selectedSubmission.email}
                      </a>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={selectedSubmission.status}
                        onChange={(e) => updateStatus(selectedSubmission.id, e.target.value)}
                        className="px-3 py-1.5 border border-[#FF6B35]/20 rounded-lg text-sm focus:outline-none focus:border-[#FF6B35]"
                      >
                        <option value="new">New</option>
                        <option value="read">Read</option>
                        <option value="replied">Replied</option>
                        <option value="archived">Archived</option>
                      </select>
                      <button
                        onClick={() => deleteSubmission(selectedSubmission.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        data-testid="delete-submission-btn"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>

                  {/* Contact Submission Details */}
                  {activeTab === 'contact' && (
                    <>
                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="p-4 bg-[#FFFBF5] rounded-xl">
                          <p className="text-xs text-[#8A8A8A] mb-1">Service Interested</p>
                          <p className="text-[#2D2D2D] font-medium">{selectedSubmission.service || 'Not specified'}</p>
                        </div>
                        <div className="p-4 bg-[#FFFBF5] rounded-xl">
                          <p className="text-xs text-[#8A8A8A] mb-1">Submitted</p>
                          <p className="text-[#2D2D2D] font-medium">{formatDate(selectedSubmission.submitted_at)}</p>
                        </div>
                      </div>

                      <div className="mb-6">
                        <p className="text-xs text-[#8A8A8A] mb-2">Message</p>
                        <div className="p-4 bg-[#FFFBF5] rounded-xl border-l-4 border-[#FF6B35]">
                          <p className="text-[#5A5A5A] whitespace-pre-wrap leading-relaxed">
                            {selectedSubmission.message}
                          </p>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Audit Submission Details */}
                  {activeTab === 'audit' && (
                    <>
                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="p-4 bg-[#FFFBF5] rounded-xl">
                          <div className="flex items-center gap-2 mb-1">
                            <Briefcase size={12} className="text-[#8A8A8A]" />
                            <p className="text-xs text-[#8A8A8A]">Company</p>
                          </div>
                          <p className="text-[#2D2D2D] font-medium">{selectedSubmission.company || 'Not specified'}</p>
                        </div>
                        <div className="p-4 bg-[#FFFBF5] rounded-xl">
                          <div className="flex items-center gap-2 mb-1">
                            <Globe size={12} className="text-[#8A8A8A]" />
                            <p className="text-xs text-[#8A8A8A]">Website</p>
                          </div>
                          {selectedSubmission.website && selectedSubmission.website !== 'Not specified' ? (
                            <a 
                              href={selectedSubmission.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#FF6B35] font-medium hover:underline break-all"
                            >
                              {selectedSubmission.website}
                            </a>
                          ) : (
                            <p className="text-[#2D2D2D] font-medium">Not specified</p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="p-4 bg-[#FFFBF5] rounded-xl">
                          <div className="flex items-center gap-2 mb-1">
                            <AlertCircle size={12} className="text-[#8A8A8A]" />
                            <p className="text-xs text-[#8A8A8A]">Focus Area</p>
                          </div>
                          <p className="text-[#2D2D2D] font-medium capitalize">{selectedSubmission.vertical?.replace('-', ' ') || 'Not specified'}</p>
                        </div>
                        <div className="p-4 bg-[#FFFBF5] rounded-xl">
                          <p className="text-xs text-[#8A8A8A] mb-1">Submitted</p>
                          <p className="text-[#2D2D2D] font-medium">{formatDate(selectedSubmission.submitted_at)}</p>
                        </div>
                      </div>

                      <div className="mb-6">
                        <p className="text-xs text-[#8A8A8A] mb-2">Challenges & Notes</p>
                        <div className="p-4 bg-[#FFFBF5] rounded-xl border-l-4 border-[#FF6B35]">
                          <p className="text-[#5A5A5A] whitespace-pre-wrap leading-relaxed">
                            {selectedSubmission.challenges || 'No additional notes provided.'}
                          </p>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="flex items-center gap-3">
                    <a
                      href={`mailto:${selectedSubmission.email}?subject=Re: Your ${activeTab === 'audit' ? 'Audit Request' : 'inquiry'} to Buzz Craft Maven`}
                      className="btn-sunrise px-6 py-3 inline-flex items-center gap-2"
                      onClick={() => updateStatus(selectedSubmission.id, 'replied')}
                    >
                      <Mail size={18} />
                      Reply via Email
                    </a>
                    <a
                      href={`https://mail.google.com/mail/?view=cm&fs=1&to=${selectedSubmission.email}&su=Re: Your ${activeTab === 'audit' ? 'Audit Request' : 'inquiry'} to Buzz Craft Maven`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-6 py-3 border border-[#FF6B35] text-[#FF6B35] rounded-full hover:bg-[#FF6B35] hover:text-white transition-colors inline-flex items-center gap-2"
                      onClick={() => updateStatus(selectedSubmission.id, 'replied')}
                    >
                      Open in Gmail
                    </a>
                  </div>
                </motion.div>
              ) : (
                <div className="bg-white rounded-2xl border border-[#FF6B35]/10 p-12 text-center">
                  <Mail size={64} className="mx-auto text-[#8A8A8A]/30 mb-4" />
                  <h3 className="text-xl text-[#5A5A5A] mb-2">Select a submission</h3>
                  <p className="text-[#8A8A8A]">Click on a submission from the list to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Admin;
